/**
 * The authored `grammarFocus` is an example dialogue, not a single question. Derive a clean, natural
 * OPENING line the teacher SAYS before the conversation starts — like a real teacher, never a drill.
 * The opener is spoken BEFORE any round-trip, so the mobile app and the web live-view preview both
 * need this exact logic — one implementation, imported twice, keeps them from drifting.
 *
 * Phase 1 has NO on-screen pictures, so the opener must never ask the child to identify an unshown
 * thing ("What is it?"). Rules, in order:
 *  0. Greeting / self-introduction slide (lead line is "Hi! I'm X" / "My name is X") → greet and ask
 *     the child's OWN name ("Hello! What's your name?"). Never make the child repeat a character name
 *     like "I'm Lucy" — {{Mary}}/{{Lucy}} are example names, not the child's.
 *  1. Lead line is a self-contained PERSONAL question (mentions you/your, e.g. "What is your name?") →
 *     ask it directly. It needs no picture.
 *  2. Otherwise SAY the teacher's first line naturally — prefer the first teacher question, else model
 *     the first teacher statement as-is. No "Let's practice! Can you say:" wrapper.
 * Drops `A:`/`B:` labels and `(teacher notes)`, renders `{{word}}` slots to their example word, strips
 * legacy `_` blanks, and takes ONE side of `/` alternations (tight `this/that` and spaced `Yes, I am / No`).
 *
 * Pure string logic — no DOM, no React Native, no Node APIs.
 */
export function deriveOpeningLine(grammarFocus?: string | null): string {
  const fallback = "Hello! What's your name?";
  if (!grammarFocus) return fallback;
  const clean = (s: string) => {
    const out = s
      .replace(/^\s*[ABab]\s*:\s*/, "") // drop a leading "A:" / "B:" speaker label
      .replace(/\([^)]*\)/g, "") // drop (teacher notes) — never spoken
      .replace(/\{\{(.*?)\}\}/g, "$1"); // {{dog}} -> dog
    // Preserve the question mark BEFORE collapsing alternations, so a spaced alternation in a
    // question ("Do you like A / B?") keeps the "?" instead of dropping it with the second option.
    const qIdx = out.indexOf("?");
    const beforeQ = qIdx >= 0 ? out.slice(0, qIdx) : out;
    const tail = qIdx >= 0 ? "?" : "";
    const firstAlt = beforeQ.split(/\s+\/\s+/)[0] ?? beforeQ; // spaced alt "Yes, I am / No" -> "Yes, I am"
    return `${firstAlt
      .replace(/([A-Za-z'’]+)\/([A-Za-z'’]+)/g, "$1") // tight alternation this/that -> this
      .replace(/_+/g, " ") // never speak a legacy "_" blank
      .replace(/\s{2,}/g, " ")
      .trim()}${tail}`;
  };
  const rawLines = grammarFocus
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (rawLines.length === 0) return fallback;

  const leadClean = clean(rawLines[0] ?? "");

  // 0. Greeting / self-introduction → open like a teacher meeting a child: greet and ask the child's
  //    REAL name. (Opening from the script would force the child to say a character name, e.g. "I'm Lucy".)
  const isGreeting =
    /\bmy name is\b/i.test(leadClean) ||
    (/^(hi|hello|hey)\b/i.test(leadClean) && /\b(i'?m|i am)\b/i.test(leadClean));
  if (isGreeting) return fallback;

  // 1. Self-contained personal question (about the child) — safe to ask as-is, no picture needed.
  //    Only the LEAD line qualifies: a personal question on a LATER line is a downstream target to
  //    reach in order, not the opener — jumping to it would break the teacher-led conversation order.
  if (leadClean.includes("?") && /\byou(r)?\b/i.test(leadClean)) {
    const qIdx = leadClean.indexOf("?");
    return qIdx >= 0 ? leadClean.slice(0, qIdx + 1) : leadClean;
  }

  // 2. Otherwise open by SAYING the teacher's first line naturally (no "Can you say:" wrapper).
  //    Prefer a teacher (non-B:) question; else model the first teacher statement as-is.
  const isTeacherLine = (l: string) => !/^\s*B\s*:/i.test(l);
  const teacherLines = rawLines.filter(isTeacherLine).map(clean);
  const firstQuestion = teacherLines.find((l) => l.includes("?"));
  if (firstQuestion) {
    const qIdx = firstQuestion.indexOf("?");
    return qIdx >= 0 ? firstQuestion.slice(0, qIdx + 1) : firstQuestion;
  }
  const firstStatement = teacherLines.find((l) => l.length > 0) ?? clean(rawLines[0] ?? "");
  return firstStatement || fallback;
}
